import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useProjects } from '../contexts/ProjectContext';
import { useToast } from '../contexts/ToastContext';
import { CapacityPlan, CapacityPlanParams, PlanDiscipline, PLAN_DISCIPLINES } from '../types';
import {
  loadPlan, savePlan, computePlanMetrics, resizePlan, weekLabels, createDefaultPlan,
} from '../utils/capacityPlan';
import { AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import { BarChartIcon, UsersIcon, ClockIcon, SaveIcon } from '../components/icons/Icons';
import { useChartColors } from '../hooks';
import { PlanCharts } from '../components/charts/PlanCharts';
import { exportPlanPdf, exportPlanCsv } from '../utils/reportExport';
import './PlanningPage.css';

const fmtHours = (h: number) => `${Math.round(h).toLocaleString()} h`;

const PARAM_FIELDS: { key: keyof CapacityPlanParams; label: string; note: string; min: number; step: number }[] = [
  { key: 'hoursPerShift', label: 'Hours / Shift', note: 'Editable', min: 1, step: 1 },
  { key: 'daysPerWeek', label: 'Days / Week', note: 'Editable', min: 1, step: 1 },
  { key: 'activeShifts', label: 'Active Shifts', note: '1 = Base, 2 = Recovery', min: 1, step: 1 },
  { key: 'shiftMultiplier', label: 'Shift Multiplier', note: 'Applied to man-hours', min: 0.1, step: 0.1 },
];

export const PlanningPage: React.FC = () => {
  const { activeProject } = useProjects();
  const { showToast } = useToast();
  const chartColors = useChartColors();
  const [plan, setPlan] = useState<CapacityPlan | null>(null);

  // Load (and date-sync) the plan whenever the active project changes.
  useEffect(() => {
    if (!activeProject) { setPlan(null); return; }
    setPlan(loadPlan(activeProject.id));
  }, [activeProject]);

  const metrics = useMemo(() => (plan ? computePlanMetrics(plan) : null), [plan]);
  const labels = useMemo(() => (plan ? weekLabels(plan.weekCount) : []), [plan]);

  const persist = useCallback((next: CapacityPlan) => {
    setPlan(next);
    savePlan(next);
  }, []);

  const setParam = (key: keyof CapacityPlanParams, value: number) => {
    if (!plan) return;
    persist({ ...plan, params: { ...plan.params, [key]: value } });
  };

  const setCell = (discipline: PlanDiscipline, week: number, value: number) => {
    if (!plan) return;
    const staffing = { ...plan.staffing, [discipline]: [...plan.staffing[discipline]] };
    staffing[discipline][week] = Math.max(0, Math.round(value) || 0);
    persist({ ...plan, staffing });
  };

  // Weeks are fully manual — this planner never reads or writes the project's
  // own schedule. Existing headcount is preserved when the count changes.
  const setWeekCount = (value: number) => {
    if (!plan) return;
    const next = Math.min(Math.max(Math.round(value) || 1, 1), 52);
    persist(resizePlan(plan, next));
  };

  const setDisciplineLabel = (discipline: PlanDiscipline, label: string) => {
    if (!plan) return;
    persist({ ...plan, disciplineLabels: { ...plan.disciplineLabels, [discipline]: label } });
  };

  const resetPlan = () => {
    if (!activeProject) return;
    persist(createDefaultPlan(activeProject.id));
    showToast('Plan reset', 'info');
  };

  if (!activeProject) {
    return (
      <div className="planning-page">
        <AppSectionHero icon={<BarChartIcon size={28} />} title="Planning" subtitle="No active project." />
        <p className="text-muted-center">No active project selected.</p>
      </div>
    );
  }
  if (!plan || !metrics) return null;

  return (
    <div className="planning-page">
      <AppSectionHero
        icon={<BarChartIcon size={28} />}
        title="Capacity Planning"
        subtitle={<>{activeProject.name} {'—'} <strong>{activeProject.code}</strong></>}
        aside={
          <>
            <AppStatCard icon={<ClockIcon size={20} />} label="Total Man-Hours" value={fmtHours(metrics.totalManHours)} caption={`${plan.weekCount} weeks planned`} tone="blue" />
            <AppStatCard icon={<UsersIcon size={20} />} label="Peak Week" value={metrics.peakWeekHeadcount} caption="Max headcount / week" tone="mint" />
          </>
        }
      />

      {/* Parameters */}
      <div className="planning-section">
        <div className="planning-section-head">
          <h2 className="planning-section-title">Shift Parameters</h2>
          <div className="planning-actions">
            <div className="planning-weeks-control" title="Number of planned weeks">
              <span>Weeks</span>
              <button className="planning-step-btn" onClick={() => setWeekCount(plan.weekCount - 1)} disabled={plan.weekCount <= 1} aria-label="Remove a week">−</button>
              <input
                type="number"
                className="planning-weeks-input"
                min={1}
                max={52}
                value={plan.weekCount}
                onChange={e => setWeekCount(Number(e.target.value))}
              />
              <button className="planning-step-btn" onClick={() => setWeekCount(plan.weekCount + 1)} disabled={plan.weekCount >= 52} aria-label="Add a week">+</button>
            </div>
            <button className="planning-btn subtle" onClick={resetPlan}>Reset</button>
            <button className="planning-btn subtle" onClick={() => exportPlanCsv(plan, metrics, labels, activeProject.name)}>&#8595; CSV</button>
            <button className="planning-btn primary" onClick={() => { void exportPlanPdf(plan, metrics, labels, activeProject.name); }}><SaveIcon size={14} /> PDF</button>
          </div>
        </div>
        <div className="planning-params">
          {PARAM_FIELDS.map(f => (
            <label key={f.key} className="planning-param">
              <span className="planning-param-label">{f.label}</span>
              <input
                type="number"
                className="planning-param-input"
                min={f.min}
                step={f.step}
                value={plan.params[f.key]}
                onChange={e => setParam(f.key, Number(e.target.value))}
              />
              <span className="planning-param-note">{f.note}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Staffing grid */}
      <div className="planning-section">
        <h2 className="planning-section-title">Weekly Staffing (headcount by discipline)</h2>
        <div className="planning-grid-wrap">
          <table className="planning-grid">
            <thead>
              <tr>
                <th className="sticky-col">Discipline</th>
                {labels.map((w, i) => <th key={i}>{w}</th>)}
                <th className="planning-grid-total">Total h</th>
              </tr>
            </thead>
            <tbody>
              {PLAN_DISCIPLINES.map(d => {
                const row = metrics.byDiscipline.find(b => b.key === d.key)!;
                return (
                  <tr key={d.key}>
                    <td className="sticky-col planning-grid-name">
                      <input
                        className="planning-discipline-input"
                        value={row.label}
                        onChange={e => setDisciplineLabel(d.key, e.target.value)}
                        aria-label={`Rename ${d.label} discipline`}
                      />
                    </td>
                    {plan.staffing[d.key].map((hc, w) => (
                      <td key={w}>
                        <input
                          type="number"
                          min={0}
                          className="planning-cell-input"
                          value={hc || ''}
                          placeholder="0"
                          onChange={e => setCell(d.key, w, Number(e.target.value))}
                        />
                      </td>
                    ))}
                    <td className="planning-grid-total">{fmtHours(row.total)}</td>
                  </tr>
                );
              })}
              <tr className="planning-grid-summary">
                <td className="sticky-col">Total Personnel</td>
                {metrics.weeklyHeadcount.map((n, i) => <td key={i}>{n}</td>)}
                <td className="planning-grid-total">—</td>
              </tr>
              <tr className="planning-grid-summary">
                <td className="sticky-col">Weekly Man-Hours</td>
                {metrics.weeklyManHours.map((n, i) => <td key={i}>{Math.round(n).toLocaleString()}</td>)}
                <td className="planning-grid-total strong">{fmtHours(metrics.totalManHours)}</td>
              </tr>
              <tr className="planning-grid-summary">
                <td className="sticky-col">Cumulative</td>
                {metrics.cumulativeManHours.map((n, i) => <td key={i}>{Math.round(n).toLocaleString()}</td>)}
                <td className="planning-grid-total">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <PlanCharts metrics={metrics} labels={labels} colors={chartColors} />
    </div>
  );
};
